import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MultiInput, MultiInputType, ProviderMapper, ApiError } from '../Components/Combined';
import { BackButton, Input, Select, LengthOfStay, Checkbox, ErrorAlert, ErrorMessage } from '../Components/Simple';
import useAsyncPricerApi from '../Hooks/useAsyncPricerApi';
import { genderValues, poaValues, serviceModeValues } from '../Constants/dropdownValues';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { LTCH as modelType } from '../Constants/modelTypes';
import useFiscalYear from '../Hooks/useFiscalYear';
import useDrgGrouperApi from '../Hooks/useDrgGrouperApi';
import { useNavigate, useLocation } from 'react-router-dom';
import useSessionState from '../Hooks/useSessionState';
import useCollection from '../Hooks/useCollection';
import useExportRequest from '../Hooks/useExportRequest';
import useImportRequest from '../Hooks/useImportRequest';
import { formatDateStringIfMatches, getDefaultDateString } from '../Utils/stringUtils';
import Button from '../Components/Simple/Button';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import { mapToSetters } from '../Utils/mapping';
import { enableAsyncPricingApi } from '../Utils/features';
import usePricerApi from '../Hooks/usePricerApi';
import { EXECUTE } from '../Constants/serviceMode';

export default function LTCH() {
  const location = useLocation();
  const { prePopulatedMedicareId, prePopulatedNPI } = location.state || {};
  const [claimId, setClaimId] = useSessionState('', 'claimId');
  const [providerName, setProviderName] = useSessionState('', 'providerName');
  const [medicareId, setMedicareId] = useSessionState(prePopulatedMedicareId ?? '', 'medicareId');
  const [npi, setNpi] = useSessionState(prePopulatedNPI ?? '', 'npi');
  const [drgCode, setDRGCode] = useSessionState('', 'drg');
  const [drgGrouperDesc, setDRGGrouperDesc] = useSessionState('', 'description');
  const [drgGrouperVersion, setDRGGrouperVersion] = useSessionState('', 'grouperVersion');
  const [drgCodeAutofilled, setDRGCodeAutofilled] = useState(false);
  const [drgSuccess, setDRGSuccess] = useState(false);
  const [dischargeStatus, setDischargeStatus] = useSessionState('', 'dischargeStatus');
  const [birthDate, setBirthDate] = useSessionState('', 'birthDate');
  const [gender, setGender] = useSessionState('', 'gender');
  const [admitDate, setAdmitDate] = useSessionState(getDefaultDateString(), 'admitDate');
  const [dischargeDate, setDischargeDate] = useSessionState(getDefaultDateString(), 'dischargeDate');
  const [fiscalYear, setFiscalYearInputDate] = useFiscalYear(null);
  const [billedAmountTotal, setBilledAmountTotal] = useSessionState('', 'billedAmountTotal');
  const [nonCoveredDays, setNonCoveredDays] = useSessionState(0, 'nonCoveredDays');
  const [siteNeutral, setSiteNeutral] = useSessionState(false, 'siteNeutral');
  const [dppOverride, setDppOverride] = useSessionState(false, 'dppOverride');
  const [drgServiceMode, setDrgServiceMode] = useSessionState(EXECUTE, 'drgServiceMode');

  const navigate = useNavigate();

  const getNewProcedureCode = () => {
    return { procedureCode: '' };
  };

  const getNewDisanosisCode = () => {
    return {
      diagnosisCode: '',
      poa: ''
    };
  };

  const DEFAULT_PROCEDURE_CODES = [getNewProcedureCode(), getNewProcedureCode(), getNewProcedureCode(), getNewProcedureCode(), getNewProcedureCode()];
  const DEFAULT_DIAGNOSIS_CODES = [getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode()];

  const proceduresCollection = useCollection(DEFAULT_PROCEDURE_CODES, getNewProcedureCode, 'procedureCodes');
  const diagnosisesCollection = useCollection(DEFAULT_DIAGNOSIS_CODES, getNewDisanosisCode, 'diagnosisCodes');

  const pricer = enableAsyncPricingApi ? useAsyncPricerApi(modelType) : usePricerApi(modelType);
  const warmupPricer = useAsyncPricerApi(modelType, true);

  const drg = useDrgGrouperApi();

  const [fileExporting, _fileExportSuccess, fileExportError, fileExportExecute, fileExportReset] = useExportRequest(modelType, {
    "claimId": claimId,
    "providerName": providerName,
    "medicareId": medicareId,
    "npi": npi,
    "drgCode": drgCode,
    "dischargeStatus": dischargeStatus,
    "birthDate": birthDate,
    "gender": gender,
    "admitDate": admitDate,
    "dischargeDate": dischargeDate,
    "billedAmountTotal": billedAmountTotal,
    "nonCoveredDays": nonCoveredDays,
    "siteNeutral": siteNeutral,
    "dppOverride": dppOverride,
    "diagnosisCodes": diagnosisesCollection.values,
    "procedureCodes": proceduresCollection.values,
    drgServiceMode
  });

  const handleFileSave = () => {
    resetApis();
    fileExportExecute();
  };

  const [fileImporting, fileImportSuccess, fileImportError, fileImportResult, fileImportReset, fileImportOnChange] = useImportRequest(modelType);

  const handleFileImportOnChange = (e) => {
    resetApis();
    fileImportOnChange(e);
  };

  const handleSubmit = async () => {
    drg.reset(); // don't want to be showing error messages
    fileExportReset();
    fileImportReset();

    if (enableAsyncPricingApi) {
      await pricer.execute(formatRequest());
    } else {
      await pricer.execute(fiscalYear, formatRequest());
    }
  };

  const formatRequest = () => {
    return {
      claimId: claimId || null,
      medicareId: medicareId || null,
      paymentMethodology: "FFS",
      dob: formatDateStringIfMatches(birthDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
      gender: gender || null,
      dischargeStatus: dischargeStatus || null,
      fromDate: formatDateStringIfMatches(admitDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
      toDate: formatDateStringIfMatches(dischargeDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
      drg: drgCode || null,
      siteNeutral: siteNeutral ? 'Y' : 'N',
      dppOverride: dppOverride ? 'Y' : 'N',
      nonCoveredDays: nonCoveredDays || 0,
      drgServiceMode,
      diagnosis: [
        ...diagnosisesCollection.values.filter(dc => dc.diagnosisCode).map(dc => {
          return {
            icdDiag: dc.diagnosisCode.toUpperCase() || null,
            poa: dc.poa || null
          }
        })
      ],
      icdProc: [
        ...proceduresCollection.values.filter(pc => pc.procedureCode).map(pc => pc.procedureCode.toUpperCase())
      ],
      serviceLineItems: [
        {
          lineNumber: 1,
          revCode: '0121',
          billed: isNaN(billedAmountTotal) ? 0 : Number(billedAmountTotal)
        }
      ]
    };
  }

  const handleReset = () => {
    setClaimId('');
    setProviderName('');
    setMedicareId('');
    setNpi('');
    setDRGCode('');
    setDRGGrouperDesc('');
    setDRGGrouperVersion('');
    setDRGSuccess('');
    setDischargeStatus('');
    setAdmitDate(getDefaultDateString());
    setDischargeDate(getDefaultDateString());
    setBirthDate('');
    setGender('');
    setBilledAmountTotal('');
    proceduresCollection.setValues(DEFAULT_PROCEDURE_CODES);
    diagnosisesCollection.setValues(DEFAULT_DIAGNOSIS_CODES);
    setNonCoveredDays(0);
    setSiteNeutral(false);
    setDppOverride(false);
    resetApis();
    setDrgServiceMode(EXECUTE);
    fileExportReset();
    fileImportReset();
  };

  const resetApis = () => {
    pricer.reset();
    drg.reset();
  };

  useEffect(() => {
    if (fileImportSuccess === true) {
      const setters = {
        "claimId": setClaimId,
        "providerName": setProviderName,
        "medicareId": setMedicareId,
        "npi": setNpi,
        "drgCode": setDRGCode,
        "dischargeStatus": setDischargeStatus,
        "birthDate": setBirthDate,
        "gender": setGender,
        "admitDate": setAdmitDate,
        "dischargeDate": setDischargeDate,
        "billedAmountTotal": setBilledAmountTotal,
        "nonCoveredDays": setNonCoveredDays,
        "siteNeutral": setSiteNeutral,
        "dppOverride": setDppOverride,
        "diagnosisCodes": diagnosisesCollection.setValues,
        "procedureCodes": proceduresCollection.setValues,
        drgServiceMode: setDrgServiceMode
      };
      handleReset();
      mapToSetters(fileImportResult, setters);
    }
  }, [fileImportSuccess]);

  useEffect(() => {
    setFiscalYearInputDate(dischargeDate);
  }, [dischargeDate]);

  useEffect(() => {
    if (drg.success && drg.result) {
      setDRGCode((drg.result.finalDRG && drg.result.finalDRG.code && drg.result.finalDRG.code.toString()) || '');
      setDRGGrouperDesc((drg.result.finalDRG.description));
      setDRGGrouperVersion((drg.result.grouperVersion));
      setDRGSuccess((drg.success));
      setDRGCodeAutofilled(true);
      drg.reset();
      setTimeout(() => {
        setDRGCodeAutofilled(false);
      }, 3000);
    }
  }, [drg.success]);

  const handleRunDrgGrouper = () => {
    pricer.reset();
    fileExportReset();
    fileImportReset();
    drg.execute({
      birthDate,
      gender,
      admitDate,
      dischargeDate,
      dischargeStatus,
      diagnosisCodes: diagnosisesCollection.values,
      procedureCodes: proceduresCollection.values
    });
  };

  useEffect(() => {
    if (pricer.success) {
      navigate('result', {
        state: {
          result: pricer.result,
          drgServiceMode,
          fiscalYear
        }
      });
    }
  }, [pricer.success]);

  const siteNeutralInformation = useMemo(() => {
    return (
      <ol type="a">
        <li>Medicare pays LTCH discharges at a site neutral payment rate when these specific criteria are not met:
          <ol type="i">
            <li>The patient was admitted within 1 day of discharge from an acute care hospital</li>
            <li>The LTCH stay did not include any psychiatric or rehab DRGs (876, 880 - 887, 894 - 897, 945 or 946)</li>
            <li>At least one of the following must be true
              <ol type="1">
                <li>The acute care hospital stay included at least 3 days in an ICU or CCU</li>
                <li>At least 96 hours of ventilation services provided at the LTCH, identified by ICD Proc Code "5A1955Z"</li>
              </ol>
            </li>
          </ol>
        </li>
        <li>Per the CARES Act, LTCHs are not paid site neutral rates during the COVID public health emergency.</li>
      </ol>
    );
  }, []);

  const dppInformation = useMemo(() => {
    return (
      <ol type="a">
        <li>An LTCH's discharge payment percentage (DPP) is its ratio of FFS discharges paid at the standard LTCH PPS Federal rate to the LTCH's total number of Medicare discharges. CMS notifies an LTCH when its DPP falls below 50%, at which point all discharges in subsequent cost-reporting periods are paid an adjusted amount until reinstated as an LTCH. Enter “Y” if the LTCH had a DPP below 50% in a prior reporting period and has not yet been reinstated; otherwise enter "N". Note that:
          <ol type="i">
            <li>Setting this to "Y" effectively makes the LTCH payment follow IPPS pricing rules</li>
            <li>Per the CARES Act, the DPP rule is waved during the COVID public health emergency, meaning that rates will not be adjusted to the IPPS rate.</li>
          </ol>
        </li>
      </ol>
    );
  }, []);

  return (
    <div className="py-2">
      <div className="row my-2 justify-content-between sticky top g-0 border-bottom align-items-center">
        <div className="col-auto">
          <h3>LTCH Pricer</h3>
          <div className="fs-6">
            Enter all claim information requested below. Please press SUBMIT when complete to calculate the prospective payment amount.
          </div>
        </div>
        <div className="col-auto">
          <BackButton />
        </div>
      </div>
      {(pricer.error || drg.error || fileImportError || fileExportError) && (<div>
        <ApiError error={pricer.error} />
        <ApiError error={drg.error} />
        {fileImportError ? <ErrorMessage errors={[{ message: fileImportError }]} /> : null}
        {fileExportError ? <ErrorMessage errors={[{ message: fileExportError }]} /> : null}
      </div>)}
      {(drgSuccess) && (<div className="alert alert-success" role="alert">
        DRG Grouper Message: {drgGrouperDesc ?? null} <br />
        DRG Grouper Version Applied: {drgGrouperVersion ?? null}
      </div>)}
      <form noValidate={true}>
        <div className="row my-2">
          <div className="col-md-3">
            <Input id="claim-id" label="Claim ID" labelClassName="fw-bold" onChange={setClaimId} value={claimId} />
          </div>
          <ProviderMapper inputs={[
            {
              name: "providerName",
              label: "Provider Name",
              superscript: "1",
              value: providerName,
              onChange: setProviderName
            },
            {
              name: "medicareId",
              label: "Medicare ID",
              superscript: "1",
              value: medicareId,
              onChange: setMedicareId
            },
            {
              name: "npi",
              label: "NPI",
              sublabel: "(facilities may have multiple NPIs)",
              superscript: "1",
              value: npi,
              onChange: setNpi
            }
          ]} model={modelType} />
        </div>
        <div className="row my-2">
          <div className="col-md-3">
            <Input label="MS-DRG" id="ms-drg" labelClassName="fw-bold" onChange={setDRGCode} value={drgCode} placeholder="000" maxLength={3} className={drgCodeAutofilled ? 'autofilled' : ''} />
          </div>
          {enableAsyncPricingApi && <div className="col-md-3">
            <Select id="drg-service-mode" value={drgServiceMode} labelClassName="fw-bold" onChange={setDrgServiceMode} label="Set DRG Grouper Execution">
              {serviceModeValues.map((v, i) => <option key={i} value={v.value}>{v.text}</option>)}
            </Select>
          </div>}
          <div className="col-md-3">
            <Input label="Discharge Status" labelClassName="fw-bold" id="discharge-status" onChange={setDischargeStatus} value={dischargeStatus} placeholder="00" maxLength={2} />
          </div>
          <div className="col-md-3">
            <Input label="Date of Birth" preservePlaceholder={true} labelClassName="fw-bold" id="date-of-birth" type="date" onChange={setBirthDate} value={birthDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
          </div>
          <div className="col-md-3">
            <Select label="Gender" id="gender" labelClassName="fw-bold" onChange={setGender} value={gender}>
              {genderValues.map(gv => <option key={gv.value} value={gv.value}>{gv.text}</option>)}
            </Select>
          </div>
        </div>
        <div className="row my-2">
          <div className="col-md-3">
            <Input label="Admit Date" preservePlaceholder={true} labelClassName="fw-bold" id="admit-date" type="date" onChange={setAdmitDate} value={admitDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
          </div>
          <div className="col-md-3">
            <Input label="Discharge Date" preservePlaceholder={true} labelClassName="fw-bold" id="discharge-date" type="date" onChange={setDischargeDate} value={dischargeDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
          </div>
          <div className="col-md-3 align-self-center mt-4">
            <LengthOfStay fromDate={admitDate} toDate={dischargeDate} nonCoveredDays={nonCoveredDays} />
          </div>
          <div className="col-md-3 align-self-center mt-4 fw-bold">
            {fiscalYear !== null && <>FY{fiscalYear}</>}
          </div>
        </div>
        <div className="row my-2">
          <div className="col-md-3">
            <Input label="Billed Amount - Total Incl" labelClassName="fw-bold" id="billed-amount-total" onChange={setBilledAmountTotal} value={billedAmountTotal} placeholder="0000.00" />
          </div>
          <div className="col-md-3">
            <Input label="Non-Covered Days" type="number" labelClassName="fw-bold" id="non-covered-days" onChange={setNonCoveredDays} value={nonCoveredDays} />
          </div>
          <div className="col-md-3 mt-4">
            <Checkbox label="Site Neutral" id="site-neutral" labelClassName="fw-bold" onChange={setSiteNeutral} value={siteNeutral} information={siteNeutralInformation} />
          </div>
          <div className="col-md-3 mt-4">
            <Checkbox label="LTCH DPP Override" id="ltch-dpp-override" labelClassName="fw-bold" onChange={setDppOverride} value={dppOverride} information={dppInformation} />
          </div>
        </div>
        <div> <sup> 1 </sup> Pricing depends only on Medicare ID </div>
        <hr />
        <div className="row g-0">
          <div className="col-md-8 my-2">
            <MultiInput label="ICD-10 Diagnosis Codes"
              note="Enter ICD codes without the decimal."
              subLabel="Diagnosis"
              collection={diagnosisesCollection}
              lineCountLabel="Total Diagnosis Codes"
              max={30}
              id="diagnosis-codes">
              <MultiInputType label="Diagnosis Code"
                name="diagnosisCode"
                type="text"
                maxLength={7} />
              <MultiInputType label="POA"
                name="poa"
                type="select"
                options={poaValues} />
            </MultiInput>
          </div>
          <div className="col-md-4 my-2">
            <MultiInput label="ICD-10 Procedure Codes"
              note="Enter ICD codes without the decimal."
              subLabel="Procedure"
              collection={proceduresCollection}
              lineCountLabel="Total Procedure Codes"
              max={30}
              id="procedure-codes">
              <MultiInputType label="Procedure Code"
                name="procedureCode"
                type="text"
                maxLength={7} />
            </MultiInput>
          </div>
        </div>
        <div className="row justify-content-between my-2 sticky bottom g-0 border-top">
          <div className="col-auto align-self-center">
            <div className="row g-0">
              <div className="col-auto ms-2">
                <Button className="btn-outline-secondary" id="save-request-button"
                  disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                  onClick={handleFileSave}
                  text="Save" />
              </div>
              <div className="col-auto ms-2">
                <FilePickerButton className="btn-outline-secondary" id="load-request-button"
                  disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                  onChange={handleFileImportOnChange}
                  loading={fileImporting}
                  text="Load"
                  accepts=".mrpreq" />
              </div>
            </div>
          </div>
          <div className="col-auto align-self-center">
            <ErrorAlert visible={pricer.error || drg.error || fileImportError || fileExportError} />
          </div>
          <div className="col-auto align-self-center">
            <div className="row g-0 justify-cotent-end">
              <div className="col-auto">
                <Button className="btn-outline-secondary" id="reset-button"
                  onClick={handleReset}
                  disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                  text="Reset" />
              </div>
              {!enableAsyncPricingApi && (<div className="col-auto ms-2">
                <Button className="btn-outline-secondary" id="run-drg-grouper-button"
                  onClick={handleRunDrgGrouper}
                  disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                  loading={drg.loading}
                  text="Run DRG Grouper" />
              </div>)}
              <div className="col-auto ms-2">
                <Button className="btn-primary" id="submit-button"
                  disabled={pricer.loading || fileImporting || fileExporting}
                  onClick={handleSubmit}
                  text="Submit"
                  loading={pricer.loading} />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
